import { useMemo } from "react";
import { Box, Text } from "@mantine/core";

import { useGetContent, useGetSessionVisibility } from "../../../api/insights";
import { useGetAllFeatureFlags } from "../../../api/featureFlags";
import { FEATURE_FLAGS } from "@congrs/schemas";
import {
  ChartFrame,
  DailyAreaChart,
  RankedBarChart,
  formatDay,
} from "../charts";
import SectionShell, { ChartPair, Stat, formatNum } from "../SectionShell";
import { useChartTimeFrame } from "../useChartTimeFrame";
import { sliceByDay, toRangeParams } from "../timeFrame";

const RatingList = ({ rows }) => (
  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
    {rows.map((s, i) => (
      <div
        key={`${s.sessionName}-${i}`}
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "20px minmax(0, 1fr) minmax(60px, 140px) 56px",
          alignItems: "center",
          gap: "var(--mantine-spacing-sm)",
          minHeight: 44,
          padding: "6px 0",
          borderTop:
            i > 0 ? "1px solid var(--mantine-color-gray-2)" : undefined,
        }}
      >
        <Text size="sm" c="dimmed" fw={600}>
          {i + 1}
        </Text>
        <div style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {s.sessionName ?? "(unknown)"}
          </Text>
          <Text size="xs" c="dimmed">
            {s.ratingCount} {s.ratingCount === 1 ? "rating" : "ratings"}
          </Text>
        </div>
        <Box
          h={8}
          style={{
            borderRadius: 4,
            overflow: "hidden",
            background: "var(--mantine-color-gray-1)",
          }}
        >
          <Box
            h="100%"
            style={{
              width: `${(Number(s.rating) / 5) * 100}%`,
              borderRadius: 4,
              background: "var(--mantine-color-blue-6)",
            }}
          />
        </Box>
        <Text size="sm" ta="right" style={{ whiteSpace: "nowrap" }}>
          <b>{Number(s.rating).toFixed(1)}</b>{" "}
          <Text span size="xs" c="dimmed">
            / 5
          </Text>
        </Text>
      </div>
    ))}
  </div>
);

function AgendaUsage({ eventId }) {
  const { data, isPending, isError, error } = useGetContent(eventId);
  const { data: flags } = useGetAllFeatureFlags({ eventId });

  const tf = useChartTimeFrame(eventId);
  const sessionsTf = useChartTimeFrame(eventId);
  const { data: sessionData } = useGetSessionVisibility(
    eventId,
    toRangeParams(sessionsTf.range)
  );

  const enabledFlagSet = useMemo(
    () =>
      new Set(
        (Array.isArray(flags) ? flags : [])
          .filter((f) => f.enabled)
          .map((f) => f.featureName)
      ),
    [flags]
  );

  const showHighestRated = enabledFlagSet.has(FEATURE_FLAGS.SESSION_RATINGS);

  const topSessions = useMemo(
    () =>
      (sessionData?.mostViewedSessions ?? []).map((s) => ({
        session: s.sessionName ?? "(unknown)",
        Views: s.viewCount,
        "Unique users": s.uniqueUsers,
        "Link clicks": s.linkClicks ?? 0,
      })),
    [sessionData]
  );
  const hasSessionLinkClicks = topSessions.some((s) => s["Link clicks"] > 0);

  const agendaOverTime = useMemo(
    () =>
      sliceByDay(data?.agendaViewsOverTime, tf.resolved).map((d) => ({
        day: formatDay(d.day),
        Views: d.viewCount,
        "Unique users": d.uniqueUsers,
      })),
    [data, tf.resolved]
  );

  const topRated = (data?.highestRatedSessions ?? []).slice(0, 10);

  return (
    <SectionShell
      id="agendaUsage"
      title="Agenda usage"
      description="Session views, bookmarks and how many sessions an attendee follows on average."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="agendaUsage"
      stats={
        <>
          <Stat
            part="bookmarks"
            label="Bookmarks"
            value={formatNum(data?.totalBookmarks)}
          />
          <Stat
            part="activeAppUsers"
            label="Active app users"
            value={formatNum(data?.totalAppUsers)}
          />
          <Stat
            part="avgSessionsFollowed"
            label="Avg sessions followed"
            value={
              Number.isFinite(Number(data?.avgSessionsFollowed))
                ? Number(data.avgSessionsFollowed).toFixed(2)
                : "—"
            }
          />
          <Stat
            part="sessionsTracked"
            label="Sessions tracked"
            value={formatNum(data?.sessionsTracked)}
          />
        </>
      }
      lead={
        <DailyAreaChart
          part="agendaViewsOverTime"
          title="Agenda views over time"
          data={agendaOverTime}
          series={[
            { name: "Views", color: "blue.6" },
            { name: "Unique users", color: "teal.6" },
          ]}
          headerRight={tf.control}
        />
      }
    >
      <ChartPair>
        <RankedBarChart
          part="mostViewedSessions"
          title="Most viewed sessions"
          data={topSessions}
          categoryKey="session"
          series={[
            ...(hasSessionLinkClicks
              ? [{ name: "Link clicks", color: "violet.3", stackId: "totals" }]
              : []),
            { name: "Unique users", color: "teal.6", stackId: "totals" },
            { name: "Views", color: "blue.6", stackId: "totals" },
          ]}
          headerRight={sessionsTf.control}
        />
        {showHighestRated && (
          <ChartFrame
            part="highestRatedSessions"
            title="Highest rated sessions"
            hasData={topRated.length > 0}
          >
            <RatingList rows={topRated} />
          </ChartFrame>
        )}
      </ChartPair>
    </SectionShell>
  );
}

export default AgendaUsage;
